import {
  Component,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ErrorInfo,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { AnimatePresence } from 'framer-motion';
import { ExternalLink, Info, Maximize2, Minimize2, Pin, PinOff, Save, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { useAppStore } from '@/app/appStore';
import { useResolvedTheme } from '@/app/theme';
import { aiApi } from '@/api/ai';
import { useChatStore } from '@/chat/chatStore';
import { createBus } from '@/panels/detachBus';
import { panelMaximizeBus } from '@/panels/panelMaximizeBus';
import { usePanelStore } from '@/panels/panelStore';
import { resolveDetachSessionId, writeDetachedBootstrap } from '@/panels/detachedState';
import { FallbackSurface } from '@/screens/FallbackSurface';
import { resolveSurface } from '@/screens/registry';
import { type PanelDescriptor, type PanelProps } from '@/screens/types';
import { PopoverPortalProvider } from '@/components/ui/popover';
import { PanelActionTooltip } from '@/shared/PanelActionTooltip';
import { PanelMotion, useReducedMotionSafe } from '@/shared/MotionWrappers';
import { buildDefaultSessionRequest } from '@/chat/sessionDefaults';

type PanelShellProps = {
  panel: PanelDescriptor;
  onClose: (id: string) => void;
  onPatchPayload: (id: string, patch: unknown) => void;
  onSaveTemplate: (cfg: unknown) => void;
};

class PanelErrorBoundaryState {
  hasError = false;
  error: Error | null = null;
}

class PanelErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  PanelErrorBoundaryState
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): PanelErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[PanelShell] surface error', error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

function resolveSupportsAskAi(entry: ReturnType<typeof resolveSurface>): boolean {
  if (!entry) return false;
  if (entry.supportsAskAi !== undefined) return entry.supportsAskAi;
  return entry.kind === 'form' || entry.kind === 'detail';
}

function stopPanelDrag(event: MouseEvent<HTMLButtonElement>): void {
  event.stopPropagation();
}

function usePanelDescription(
  panel: PanelShellProps['panel'],
  i18nNamespace: string | undefined,
): string {
  const { t: tSurface } = useTranslation(i18nNamespace ?? '');
  if (panel.description !== undefined) return panel.description;
  if (!i18nNamespace) return '';
  return tSurface('description', { defaultValue: '' }) as string;
}

export function PanelShell({ panel, onClose, onPatchPayload, onSaveTemplate }: PanelShellProps) {
  const { t } = useTranslation('errors');
  const { t: tc } = useTranslation('common');
  const [maximized, setMaximized] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiToast, setAiToast] = useState<string | null>(null);
  const reduced = useReducedMotionSafe();
  const inlineSlotRef = useRef<HTMLDivElement | null>(null);
  const overlaySlotRef = useRef<HTMLDivElement | null>(null);
  const [portalHost] = useState<HTMLDivElement | null>(() =>
    typeof document === 'undefined'
      ? null
      : (() => {
          const element = document.createElement('div');
          element.className = 'h-full w-full';
          return element;
        })(),
  );

  const { user } = useAuth();
  const sessionId = useChatStore((s) => s.sessionId);
  const setSession = useChatStore((s) => s.setSession);
  const newCorrelation = useChatStore((s) => s.newCorrelation);
  const handleUiAction = usePanelStore((s) => s.handleUiAction);
  const detachPanel = usePanelStore((s) => s.detachPanel);
  const togglePinPanel = usePanelStore((s) => s.togglePinPanel);
  const renamePanelTitle = usePanelStore((s) => s.renamePanelTitle);
  const setPanelLayout = usePanelStore((s) => s.setPanelLayout);
  const setFocusedPanel = usePanelStore((s) => s.setFocusedPanel);
  const isFocused = usePanelStore((s) => s.focusedPanelId === panel.panelId);
  const themePreference = useAppStore((s) => s.theme);
  const resolvedTheme = useResolvedTheme(themePreference);

  useEffect(() => {
    const unsub = panelMaximizeBus.subscribe((id) => {
      if (id === panel.panelId) {
        setMaximized((prev) => !prev);
      }
    });
    return unsub;
  }, [panel.panelId]);

  useEffect(() => {
    if (!maximized || typeof window === 'undefined') {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMaximized(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [maximized]);

  const toggleMaximized = useCallback(() => {
    setMaximized((value) => !value);
  }, []);

  useEffect(() => {
    if (!portalHost) {
      return;
    }

    return () => {
      portalHost.remove();
    };
  }, [portalHost]);

  useEffect(() => {
    const target = maximized ? overlaySlotRef.current : inlineSlotRef.current;
    if (!portalHost || !target) {
      return;
    }

    if (portalHost.parentElement !== target) {
      target.appendChild(portalHost);
    }
  }, [maximized, portalHost]);

  const entry = resolveSurface(panel.surface);
  const showAskAi = resolveSupportsAskAi(entry);
  const SurfaceComponent: ComponentType<PanelProps> = entry?.component ?? FallbackSurface;
  const fallbackPayload = entry == null ? { surfaceKey: panel.surface } : panel.payload;
  const handleClose = useCallback(() => onClose(panel.panelId), [onClose, panel.panelId]);
  const handlePatchPayload = useCallback(
    (next: unknown) => onPatchPayload(panel.panelId, next),
    [onPatchPayload, panel.panelId],
  );

  const panelProps: PanelProps = {
    panelId: panel.panelId,
    payload: fallbackPayload,
    onClose: handleClose,
    onSaveTemplate,
    onPatchPayload: handlePatchPayload,
  };

  const baseTitle = panel.title ?? entry?.i18nNamespace.replace(/^surface\./, '') ?? panel.surface;
  const title = panel.customTitle ?? baseTitle;
  const breadcrumb = (() => {
    try {
      return entry?.breadcrumb?.(panel.payload) ?? '';
    } catch (err) {
      console.warn('[PanelShell] breadcrumb derivation failed', err);
      return '';
    }
  })();

  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  useEffect(() => {
    if (!titleEditing) setTitleDraft(title);
  }, [title, titleEditing]);

  const commitTitle = useCallback(
    (next: string) => {
      // Persist only if the user actually changed the title from baseTitle/customTitle.
      renamePanelTitle(panel.panelId, next === baseTitle ? '' : next);
      setTitleEditing(false);
    },
    [baseTitle, panel.panelId, renamePanelTitle],
  );

  const description = usePanelDescription(panel, entry?.i18nNamespace);
  const descId = `panel-desc-${panel.panelId}`;

  async function handleAskAi() {
    if (isAiLoading) return;
    setIsAiLoading(true);
    setAiToast(null);

    const correlationId = newCorrelation();

    try {
      let sid = sessionId;
      if (!sid) {
        const session = await aiApi.createSession(buildDefaultSessionRequest(), { correlationId });
        sid = session.session_id;
        setSession(sid);
      }

      const res = await aiApi.sendMessage(
        {
          session_id: sid,
          message: tc('panelActions.askAiPrompt'),
          context: { surface: panel.surface, payload: panel.payload },
        },
        { correlationId },
      );

      const action = res.ui_action;
      if (action && (action.type === 'update_panel' || action.type === 'open_panel')) {
        handleUiAction(action, correlationId);
      } else {
        setAiToast(tc('panelActions.askAiNoUpdates'));
        setTimeout(() => setAiToast(null), 3500);
      }
    } catch {
      setAiToast(tc('panelActions.askAiError'));
      setTimeout(() => setAiToast(null), 3500);
    } finally {
      setIsAiLoading(false);
    }
  }

  const errorFallback = (
    <div className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center">
      <p className="text-sm font-medium">{t('panelError.title')}</p>
      <p className="text-muted-foreground text-xs">{t('panelError.description')}</p>
      <button
        type="button"
        onClick={() => onClose(panel.panelId)}
        className="border-border hover:bg-muted mt-2 rounded border px-3 py-1 text-xs"
      >
        {t('panelError.backToChat')}
      </button>
    </div>
  );

  const panelBody = (
    <PopoverPortalProvider container={portalHost}>
      <div className="relative min-h-0 flex-1 overflow-auto">
        {isAiLoading && (
          <div
            data-testid="panel-ai-overlay"
            className="bg-card/70 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px]"
            style={reduced ? undefined : { transition: 'opacity 120ms ease' }}
          >
            <div
              className={
                reduced
                  ? 'border-primary h-5 w-5 rounded-full border-2 border-t-transparent'
                  : 'border-primary h-5 w-5 animate-spin rounded-full border-2 border-t-transparent'
              }
            />
          </div>
        )}

        {aiToast && (
          <div
            role="status"
            data-testid="panel-ai-toast"
            className="border-border bg-card text-muted-foreground absolute inset-x-0 top-2 z-20 mx-3 rounded border px-3 py-2 text-center text-xs shadow"
          >
            {aiToast}
          </div>
        )}

        <PanelErrorBoundary fallback={errorFallback}>
          <Suspense
            fallback={
              <div className="flex h-full items-center justify-center">
                <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
              </div>
            }
          >
            <SurfaceComponent {...panelProps} />
          </Suspense>
        </PanelErrorBoundary>
      </div>
    </PopoverPortalProvider>
  );

  const headerClassName = maximized
    ? 'flex items-center justify-between border-b border-border px-3 py-2'
    : 'panel-drag-handle flex cursor-move items-center justify-between border-b border-border px-3 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--wb-accent)]';

  const handleHeaderKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (maximized || titleEditing) return;
      const step = event.shiftKey ? 4 : 1;
      let dx = 0;
      let dy = 0;
      if (event.key === 'ArrowLeft') dx = -step;
      else if (event.key === 'ArrowRight') dx = step;
      else if (event.key === 'ArrowUp') dy = -step;
      else if (event.key === 'ArrowDown') dy = step;
      else return;
      event.preventDefault();
      const layout = panel.layout;
      setPanelLayout(panel.panelId, {
        x: Math.max(0, layout.x + dx),
        y: Math.max(0, layout.y + dy),
        w: layout.w,
        h: layout.h,
      });
    },
    [maximized, panel.layout, panel.panelId, setPanelLayout, titleEditing],
  );

  const panelHeader = (
    <div
      className={headerClassName}
      tabIndex={maximized ? -1 : 0}
      role={maximized ? undefined : 'group'}
      aria-label={maximized ? undefined : tc('panel.dragHandle', { defaultValue: 'Panel drag handle. Use arrow keys to reposition.' })}
      onKeyDown={handleHeaderKeyDown}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        {titleEditing ? (
          <input
            autoFocus
            value={titleDraft}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={() => commitTitle(titleDraft.trim())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitTitle(titleDraft.trim());
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setTitleDraft(title);
                setTitleEditing(false);
              }
            }}
            aria-label={tc('panel.renameHint', { defaultValue: 'Rename panel' })}
            data-testid="panel-title-input"
            className="border-border bg-background min-w-0 truncate border-b text-sm font-medium outline-none focus:border-[color:var(--wb-accent)]"
          />
        ) : (
          <h2
            className="text-foreground cursor-text truncate text-sm font-medium"
            onDoubleClick={() => setTitleEditing(true)}
            title={tc('panel.renameHint', { defaultValue: 'Double-click to rename' })}
            data-testid="panel-title"
          >
            {title}
          </h2>
        )}
        {breadcrumb ? (
          <span
            className="bg-muted text-muted-foreground rounded px-1.5 text-xs"
            data-testid="panel-breadcrumb"
          >
            {breadcrumb}
          </span>
        ) : null}
        {panel.badge ?? null}
        {description ? (
          <PanelActionTooltip label={description}>
            <span
              role="img"
              aria-label={tc('panelActions.info', { defaultValue: 'Panel information' })}
              className="text-muted-foreground flex-shrink-0"
              data-testid="panel-info-icon"
            >
              <Info size={12} aria-hidden="true" />
            </span>
          </PanelActionTooltip>
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        {panel.headerActions ?? null}
        {showAskAi && (
          <PanelActionTooltip label={tc('panelActions.askAi')}>
            <button
              type="button"
              aria-label={tc('panelActions.askAi')}
              onMouseDown={stopPanelDrag}
              onClick={() => {
                void handleAskAi();
              }}
              disabled={isAiLoading}
              data-testid="panel-ask-ai-btn"
              className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1 disabled:opacity-50"
            >
              <Sparkles size={14} />
            </button>
          </PanelActionTooltip>
        )}
        <PanelActionTooltip
          label={
            panel.pinned
              ? tc('panel.unpin', { defaultValue: 'Unpin panel' })
              : tc('panel.pin', { defaultValue: 'Pin panel' })
          }
        >
          <button
            type="button"
            aria-label={
              panel.pinned
                ? tc('panel.unpin', { defaultValue: 'Unpin panel' })
                : tc('panel.pin', { defaultValue: 'Pin panel' })
            }
            aria-pressed={panel.pinned ?? false}
            onMouseDown={stopPanelDrag}
            onClick={() => togglePinPanel(panel.panelId)}
            data-testid="panel-pin-btn"
            className={
              panel.pinned
                ? 'rounded p-1 text-[color:var(--wb-accent)] hover:bg-muted'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1'
            }
          >
            {panel.pinned ? <PinOff size={14} /> : <Pin size={14} />}
          </button>
        </PanelActionTooltip>
        <PanelActionTooltip
          label={tc('panelActions.saveTemplate', { defaultValue: 'Save as template' })}
        >
          <button
            type="button"
            aria-label={tc('panelActions.saveTemplate', { defaultValue: 'Save as template' })}
            onMouseDown={stopPanelDrag}
            onClick={() => onSaveTemplate({ surface: panel.surface, payload: panel.payload })}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            <Save size={14} />
          </button>
        </PanelActionTooltip>
        <PanelActionTooltip label={tc('panelActions.detach', { defaultValue: 'Detach panel' })}>
          <button
            type="button"
            aria-label={tc('panelActions.detach', { defaultValue: 'Detach panel' })}
            onMouseDown={stopPanelDrag}
            onClick={() => {
              const updatedAt = Date.now();
              const detached = {
                ...panel,
                detached: true,
                updatedAt,
              };

              writeDetachedBootstrap(detached, resolvedTheme);

              const popup = window.open(
                `/workbench/panel/${panel.panelId}`,
                `workbench-panel-${panel.panelId}`,
                'popup,width=1280,height=900,resizable=yes,scrollbars=yes',
              );

              if (!popup) {
                return;
              }

              detachPanel(panel.panelId, updatedAt);

              const bus = createBus(resolveDetachSessionId(sessionId, user?.sub));
              bus.publish({
                kind: 'panel_opened',
                panelId: detached.panelId,
                data: { panel: detached },
              });
              bus.publish({
                kind: 'theme_changed',
                panelId: detached.panelId,
                data: { theme: resolvedTheme },
              });
              bus.close();
            }}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            <ExternalLink size={14} />
          </button>
        </PanelActionTooltip>
        <PanelActionTooltip
          label={
            maximized
              ? tc('panelActions.restore', { defaultValue: 'Restore panel' })
              : tc('panelActions.maximize', { defaultValue: 'Maximize panel' })
          }
          shortcutId="maximizeFocusedPanel"
        >
          <button
            type="button"
            aria-label={
              maximized
                ? tc('panelActions.restore', { defaultValue: 'Restore panel' })
                : tc('panelActions.maximize', { defaultValue: 'Maximize panel' })
            }
            onMouseDown={stopPanelDrag}
            onClick={() => {
              void toggleMaximized();
            }}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            {maximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        </PanelActionTooltip>
        <PanelActionTooltip
          label={tc('panelActions.close', { defaultValue: 'Close panel (Esc)' })}
          shortcutId="closeFocusedPanel"
        >
          <button
            type="button"
            aria-label={tc('panelActions.close', { defaultValue: 'Close panel' })}
            onMouseDown={stopPanelDrag}
            onClick={() => onClose(panel.panelId)}
            className="text-muted-foreground hover:bg-muted hover:text-foreground rounded p-1"
          >
            <X size={14} />
          </button>
        </PanelActionTooltip>
      </div>
    </div>
  );

  const contextSlot = panel.contextSlot ? (
    <div className="border-border text-muted-foreground border-b px-3 py-1 text-xs">
      {panel.contextSlot}
    </div>
  ) : null;

  const panelContent = (
    <PanelMotion
      enterDelayMs={panel.enterDelayMs}
      className={`bg-card flex h-full w-full flex-col overflow-hidden rounded-lg border ${isFocused ? 'border-[color:var(--wb-accent)]' : 'border-border'} ${maximized ? 'shadow-2xl' : 'shadow-sm'}`}
      onMouseDownCapture={() => setFocusedPanel(panel.panelId)}
      onFocusCapture={() => setFocusedPanel(panel.panelId)}
      data-testid={`panel-${panel.surface}`}
      data-panel-id={panel.panelId}
      data-panel-focused={isFocused ? 'true' : 'false'}
      aria-describedby={description ? descId : undefined}
    >
      {description ? (
        <p id={descId} className="sr-only">
          {description}
        </p>
      ) : null}
      {panelHeader}
      {contextSlot}
      {panelBody}
    </PanelMotion>
  );

  return (
    <AnimatePresence>
      <>
        <div
          ref={inlineSlotRef}
          className={maximized ? 'invisible h-full w-full' : 'h-full w-full'}
          aria-hidden={maximized}
        />
        {maximized ? (
          createPortal(
            <>
              <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setMaximized(false)} />
              <FocusScope loop trapped>
                <div className="fixed inset-4 z-50">
                  <div ref={overlaySlotRef} className="h-full w-full" />
                </div>
              </FocusScope>
            </>,
            document.body,
          )
        ) : null}
        {portalHost ? createPortal(panelContent, portalHost) : null}
      </>
    </AnimatePresence>
  );
}
